import { memo, useCallback, useMemo } from 'react';
import { useAnalyticsOrgLocations, DemoChip } from '@frontend/analytics-shared';
import { CategoryBarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { trackingIds } from '../../tracking-ids';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';

type Props = {
  data?: CategoryBarChartData;
  isLoading?: boolean;
  isLocationsComparison?: boolean;
  onGroupClick?: ((locationId: string) => void) | null;
};

const pratitionerComparisonColors = {
  acceptedPercent: theme.colors.warning50,
  avgPractitioner: theme.colors.primary40,
  diagnosedPercent: theme.colors.warning50,
  practitioner: theme.colors.warning50,
  topPractitioner: theme.colors.secondary.seaweed30,
  unscheduledTreatment: theme.colors.warning50,
};

const locationsComparisonColors = {
  acceptedPercent: theme.colors.secondary.eggplant20,
  diagnosedPercent: theme.colors.secondary.eggplant20,
  unscheduledTreatment: theme.colors.secondary.eggplant20,
  yourPractice: theme.colors.secondary.eggplant20,
};

const labels = {
  acceptedPercent: i18next.t('Patients Accepted', { ns: 'analytics' }),
  avgPractitioner: i18next.t('Average Practitioner', { ns: 'analytics' }),
  diagnosedPercent: i18next.t('Patients Diagnosed', { ns: 'analytics' }),
  practitioner: i18next.t('Practitioner', { ns: 'analytics' }),
  topPractitioner: i18next.t('Top Practitioner', { ns: 'analytics' }),
  unscheduledTreatment: i18next.t('Unscheduled Treatment', { ns: 'analytics' }),
  yourPractice: i18next.t('Your Practice', { ns: 'analytics' }),
};

export const PractitionerAnalysisCategoryChart = memo(
  ({ data = {}, isLoading, isLocationsComparison, onGroupClick = null }: Props) => {
    const { t } = useTranslation('analytics');
    const { isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
      'isDemoAccount',
      'showDemoChipAndBanner'
    );
    const { locationNames } = useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });

    // Get top and total values for each key
    const processedValues = useMemo(() => {
      const values = Object.values(data || {}).reduce(
        (acc, value) => {
          acc.sum.acceptedPercent += value.acceptedPercent;
          acc.sum.diagnosedPercent += value.diagnosedPercent;
          acc.sum.unscheduledTreatment += value.unscheduledTreatment;

          acc.top.acceptedPercent = Math.max(acc.top.acceptedPercent, value.acceptedPercent);
          acc.top.diagnosedPercent = Math.max(acc.top.diagnosedPercent, value.diagnosedPercent);
          acc.top.unscheduledTreatment = Math.max(acc.top.unscheduledTreatment, value.unscheduledTreatment);

          return acc;
        },
        {
          top: {
            acceptedPercent: 0,
            diagnosedPercent: 0,
            unscheduledTreatment: 0,
          },
          sum: {
            acceptedPercent: 0,
            diagnosedPercent: 0,
            unscheduledTreatment: 0,
          },
        }
      );

      return {
        ...values,
        data,
      };
    }, [data]);

    const handleGroupClick = useCallback(
      ({ groupName }: { groupName: string }) => {
        onGroupClick?.(groupName);
      },
      [onGroupClick]
    );

    const dataLength = useMemo(() => Object.keys(processedValues.data).length, [processedValues.data]);

    return (
      <Chart
        colors={isLocationsComparison ? locationsComparisonColors : pratitionerComparisonColors}
        isLoading={isLoading}
        labels={{
          ...labels,
          ...locationNames,
          groupNameHeader: isLocationsComparison ? i18next.t('Location') : i18next.t('Practitioner'),
        }}
      >
        <Chart.Header leftElement={showDemoChipAndBanner ? <DemoChip /> : null} title={t('Practitioner Analysis')} />
        <Chart.CategoryBarChart
          appearance={{
            customTooltipTitle: ({ groupName }) => (isLocationsComparison ? locationNames[groupName] : groupName),
            formatters: {
              acceptedPercent: formatters.appendPercent,
              diagnosedPercent: formatters.appendPercent,
              unscheduledTreatment: (v) => formatters.currency(v),
            },
            maxValues: {
              acceptedPercent: Math.ceil(processedValues.top.acceptedPercent / 100) * 100,
              diagnosedPercent: Math.ceil(processedValues.top.diagnosedPercent / 100) * 100,
            },
            xAxisTickFormatters: {
              acceptedPercent: formatters.appendPercent,
              diagnosedPercent: formatters.appendPercent,
              unscheduledTreatment: (value) => formatters.shortenCurrency(value),
            },
          }}
          data={processedValues.data}
          markers={
            isLocationsComparison
              ? undefined
              : {
                  acceptedPercent: [
                    {
                      id: 'avgPractitioner',
                      value: processedValues.sum.acceptedPercent / dataLength,
                    },
                    {
                      id: 'topPractitioner',
                      value: processedValues.top.acceptedPercent,
                    },
                  ],
                  diagnosedPercent: [
                    {
                      id: 'avgPractitioner',
                      value: processedValues.sum.diagnosedPercent / dataLength,
                    },
                    {
                      id: 'topPractitioner',
                      value: processedValues.top.diagnosedPercent,
                    },
                  ],
                  unscheduledTreatment: [
                    {
                      id: 'avgPractitioner',
                      value: processedValues.sum.unscheduledTreatment / dataLength,
                    },
                    {
                      id: 'topPractitioner',
                      value: processedValues.top.unscheduledTreatment,
                    },
                  ],
                }
          }
          onGroupClick={onGroupClick && handleGroupClick}
          trackingIdBase={trackingIds.practiceAnalytics.practitionerAnalysisChart}
        />
        <Chart.Legends
          customData={
            isLocationsComparison
              ? {
                  yourPractice: {
                    label: labels['yourPractice'],
                  },
                }
              : {
                  practitioner: {
                    label: labels['practitioner'],
                  },
                  avgPractitioner: {
                    label: labels['avgPractitioner'],
                  },
                  topPractitioner: {
                    label: labels['topPractitioner'],
                  },
                }
          }
        />
      </Chart>
    );
  }
);

PractitionerAnalysisCategoryChart.displayName = 'PractitionerAnalysisCategoryChart';
